import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Typography, Form, message, Flex, Spin, Row, Col } from "antd"
import { useStripe, useElements, CardNumberElement } from "@stripe/react-stripe-js"
import EmbeddedBookingLayout from "../EmbeddedBookingLayout"
import BookingDetailCard from "./BookingDetailCard"
import { Icon } from "ui/Icon"
import ContactInformationBlock from "./ContactInformationBlock"
import EmbeddedPageHeader from "../EmbeddedPageHeader"
import { receiveErrors } from "modules/errors/reducer"
import { notification } from "ui/Notification"
import { attachUserPaymentMethod, failedPaymentMethods, selectedPaymentMethodIdSelector, checkStripeFullyConnected } from "store/user"
import {
  createEmbeddededBooking,
  fetchExperienceById,
  updateEmbeddededBooking,
  updateBookingSelections,
  createPaymentIntent,
  capturePayment,
  resetBookingInfo,
  resetBookingSelections
} from "store/experiences"
import { getPublicExperienceOptions } from "store/trips"
import PaymentBlock from "./PaymentBlock"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import { oldRoutes } from "router/old-routes"
import { useTheme } from "context/AntdThemeContext"
import { BOOKING_SOURCE, BOOKING_MODES } from "pagesV2/pro/Bookings/constants"

const { Text } = Typography

const EmbeddedExperienceBooking = ({ mode }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  // Embedded (third-party widget) must always behave as logged-out guest checkout, even when a
  // logged-in session exists in the same browser's shared localStorage.
  const accessToken = mode === BOOKING_MODES.embedded ? null : localStorage.getItem("access_token")
  const { uuid, id } = useParams()
  const state = useSelector((state) => state)
  const {
    experiences: {
      experienceById: { loading, experienceDetail },
      experienceBookingData: { bookingSelections },
      bookingInfo
    },
    user: userData
  } = state
  const [contactFormValues, setContactFormValues] = useState(null)
  useEffect(() => {
    if (!contactFormValues && userData?.email && userData?.role !== "guide_user") {
      setContactFormValues({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        gender: userData.gender || "",
        email: userData.email || "",
        phone_number: userData.phone_number || "",
        accessibility_enabled: false,
        accessibility_notes: "",
        country_code: userData.country_code || "US"
      })
    }
  }, [userData, contactFormValues])

  const [activeTab, setActiveTab] = useState(1)
  const [appliedPromos, setAppliedPromos] = useState([])
  const [isEditingAddOns, setIsEditingAddOns] = useState(false)
  const [addOnsSnapshot, setAddOnsSnapshot] = useState(null)
  // A promo change alters the amount, so any PaymentIntent created for the old total is stale.
  const handlePromoApplied = (promo) => {
    paymentIntentRef.current = null
    setAppliedPromos((prev) => [...prev, promo])
  }
  const handlePromoRemoved = (code) => {
    paymentIntentRef.current = null
    setAppliedPromos((prev) => prev.filter((p) => p.code !== code))
  }
  const handlePrevious = () => {
    // Leaving the payment step (to edit contact/add-ons) can change the total, so drop the
    // cached PaymentIntent — the next Submit will create a fresh one for the new amount.
    paymentIntentRef.current = null
    setAppliedPromos([])
    if (isEditingAddOns && addOnsSnapshot) {
      dispatch(updateBookingSelections({ addOns: addOnsSnapshot }))
    }
    setIsEditingAddOns(false)
    setAddOnsSnapshot(null)
  }
  const isStripeConnected = checkStripeFullyConnected(experienceDetail?.guide?.stripe_account_data)
  // The footer Submit button drives the card/PaymentIntent flow, which only works for a guide with
  // Stripe connected. EmbeddedBookingLayout hides Submit when this is "paypal", so derive it from
  // the guide's connected provider instead of holding it in state that nothing ever updated.
  const selectedPaymentProvider = isStripeConnected ? "stripe" : "paypal"
  const [bookingErrors, setBookingErrors] = useState({
    date: false,
    counts: false,
    tripLength: false,
    timeSlot: false
  })
  const [phoneNumber, setPhoneNumber] = useState()
  const [paymentLoading, setPaymentLoading] = useState(false)
  // Guards handleTab3 against re-entry. Once a card has been charged, this stays locked so a
  // second Submit click can never mint a new payment intent and double-charge the guest.
  const isSubmittingRef = useRef(false)
  // Holds the PaymentIntent created for the current order ({ clientSecret, incomeId }). Reused
  // across Submit retries so a dropped connection mid-charge doesn't create a second charge: on
  // retry we retrieve this intent's status instead of minting a new one. Invalidated whenever the
  // order changes (going back, applying/removing a promo).
  const paymentIntentRef = useRef(null)
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false)
  const [form] = Form.useForm()
  const stripe = useStripe()
  const elements = useElements()
  const { switchTheme } = useTheme()

  useEffect(() => {
    if (shouldScrollToTop) {
      window.scrollTo(0, 0)
      setShouldScrollToTop(false)
    }
  }, [shouldScrollToTop])

  useEffect(() => {
    if (id) dispatch(getPublicExperienceOptions(id))
  }, [dispatch, id])

  useEffect(() => {
    // Clear stale guest_token so a new booking session doesn't re-use the previous session's auth,
    // which would cause the backend to update the old booking's client instead of creating a new one.
    if (!accessToken) {
      localStorage.removeItem("guest_token")
    }
    return () => {
      dispatch(resetBookingInfo())
      dispatch(resetBookingSelections())
    }
  }, [dispatch])

  useEffect(() => {
    if (id) {
      dispatch(fetchExperienceById(id, "", mode))
    }
  }, [id, dispatch, mode])

  // Apply theme based on guide's adventure_mode
  useEffect(() => {
    if (experienceDetail?.guide?.adventure_mode) {
      switchTheme(experienceDetail.guide.adventure_mode)
    }
  }, [experienceDetail?.guide?.adventure_mode, switchTheme])

  const handleNext = async () => {
    switch (activeTab) {
      case 1:
        return handleTab2()
      case 2:
        return handleTab3()
      default:
        return
    }
  }

  const handleTab2 = async () => {
    try {
      const values = await form.validateFields({ validateOnly: false })
      const bookingId = bookingInfo?.data?.uuid
      // Multi-day experiences have no selectable time slot — the backend derives start/end time from
      // the trip schedule, so guard the split (timeSlot is null for multi-day).
      const timeSlots = bookingSelections?.timeSlot
      const [start_time, end_time] = timeSlots ? timeSlots.split(" - ") : [undefined, undefined]
      const priceData = bookingSelections?.tripLength?.price || {}
      const { pricing_type, included_guests, max_guests } = priceData

      const totalCounts = bookingSelections?.counts?.main || {}
      let extraCounts = { adults: 0, children: 0, infants: 0 } // Passengers don't have extra count logic currently
      let includedCounts

      if (pricing_type === "charge_flat_rate") {
        const guestsIncluded = included_guests ?? max_guests ?? 0
        let remainingCapacity = guestsIncluded

        const adults = Number(totalCounts.adults || 0)
        const adultsIncluded = Math.min(adults, remainingCapacity)
        remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)
        extraCounts.adults = Math.max(0, adults - adultsIncluded)

        const children = Number(totalCounts.children || 0)
        const childrenIncluded = Math.min(children, remainingCapacity)
        remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)
        extraCounts.children = Math.max(0, children - childrenIncluded)

        const infants = Number(totalCounts.infants || 0)
        const infantsIncluded = Math.min(infants, remainingCapacity)
        // remainingCapacity update removed as it's the last step
        extraCounts.infants = Math.max(0, infants - infantsIncluded)

        // Passengers are always extra in Flat Rate
        extraCounts.passengers = Number(totalCounts.passengers || 0)

        // Store included counts for payload
        includedCounts = {
          adults: adultsIncluded,
          children: childrenIncluded,
          infants: infantsIncluded,
          passengers: 0
        }
      } else {
        // Default for Per Person or other types: All considered 'included' in base count keys?
        // Or purely just total counts. User logic implies split is needed for Flat Rate.
        // For Per Person, extra is 0. So counts should be Total.
        includedCounts = {
          adults: Number(totalCounts.adults || 0),
          children: Number(totalCounts.children || 0),
          infants: Number(totalCounts.infants || 0),
          passengers: Number(totalCounts.passengers || 0)
        }
      }

      // Determine booking source based on mode and login status
      let bookingSource = BOOKING_SOURCE.embedded

      if (mode === BOOKING_MODES.embedded) {
        // Embedded mode always uses embedded source
        bookingSource = BOOKING_SOURCE.embedded
      } else if (mode === BOOKING_MODES.clientBooking || mode === BOOKING_MODES.guideProfile) {
        // For clientBooking and guideProfile modes:
        // If user is logged in, it's website booking; otherwise marketplace
        bookingSource = accessToken ? BOOKING_SOURCE.website : BOOKING_SOURCE.marketplace
      }

      const payload = {
        id,
        booking: {
          client_attributes: {
            first_name: values?.first_name?.trim(),
            last_name: values?.last_name?.trim(),
            email: values?.email?.trim(),
            phone_number: values?.phone_number?.fullNumber,
            country_code: values?.phone_number?.countryCode,
            gender: values?.gender
          },
          accessibility_enabled: values?.accessibility_enabled,
          accessibility_notes: values?.accessibility_notes,
          booked_date: bookingSelections?.date,
          adult_count: includedCounts.adults,
          child_count: includedCounts.children,
          infant_count: includedCounts.infants,
          passenger_count: includedCounts.passengers,
          start_time,
          end_time,
          trip_price_id: bookingSelections?.tripLength?.key,
          extra_adult_count: extraCounts.adults,
          extra_child_count: extraCounts.children,
          extra_infant_count: extraCounts.infants,
          extra_passenger_count: extraCounts.passengers,
          embedded: bookingId ?? !mode,
          booking_source: bookingSource,
          booking_add_ons_attributes: Object.entries(bookingSelections?.addOns || {})
            .filter(([, qty]) => Number(qty) > 0)
            .map(([addOnId, qty]) => ({ add_on_id: Number(addOnId) || addOnId, quantity: Number(qty) }))
        }
      }
      if (bookingId) {
        dispatch(updateEmbeddededBooking(bookingId, payload)).then((res) => {
          if (res?.uuid) {
            setActiveTab((prev) => prev + 1)
            setShouldScrollToTop(true)
          }
        })
      } else {
        dispatch(createEmbeddededBooking(payload)).then((res) => {
          if (res?.uuid) {
            setActiveTab((prev) => prev + 1)
            setShouldScrollToTop(true)
          }
        })
      }
    } catch (error) {
      message.error("Please fill the required data!")
      // Ant Design will automatically scroll to first error if form has scrollToFirstError enabled
      form.scrollToField(error.errorFields?.[0]?.name, {
        behavior: "smooth",
        block: "center"
      })
    }
  }

  const handleTab3 = async () => {
    // Re-entrancy guard: block concurrent/rapid Submit clicks (e.g. double-tap before the loader
    // overlay renders) so we never fire two payment intents for the same booking.
    if (isSubmittingRef.current) return
    // No Stripe on the guide's account means no card flow at all (the Submit button is hidden in
    // that case; this guards any other caller).
    if (!isStripeConnected) return
    isSubmittingRef.current = true
    try {
      // Determine which fields to validate based on payment method and user state
      const fieldsToValidate = ["paymentMethod"]
      // If user is logged in and using saved payment methods
      if (mode && accessToken) {
        // Only validate paymentMethod - card is already attached via API
        // The selectedPaymentMethodId is used for payment, not form fields
      } else {
        // For guest checkout, validate card fields (Stripe is connected — guarded above).
        fieldsToValidate.push("cardName", "cardNumber", "expiry", "cvc", "zip", "country")
      }
      await form.validateFields(fieldsToValidate)
      setPaymentLoading(true)
      const cardElement = elements.getElement(CardNumberElement)
      let paymentMethodId = null
      if (!selectedPaymentMethodId) {
        const param = {
          type: "card",
          card: cardElement,
          billing_details: {
            name: form.getFieldValue("cardName"),
            address: {
              postal_code: form.getFieldValue("zip")
            }
          }
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod(param)
        dispatch(failedPaymentMethods())
        if (error) {
          if (error.type === "card_error") {
            dispatch(
              receiveErrors({
                data: { message: { card_error: error.message } },
                showToast: false
              })
            )
          } else {
            notification.error({
              message: t("booking.error_messages.stripe_error")
            })
          }
          setPaymentLoading(false)
          isSubmittingRef.current = false
          return false
        }
        const payload = {
          new_user: false,
          id: bookingInfo?.data?.client?.id,
          payment_method_id: paymentMethod?.id,
          ...(mode === BOOKING_MODES.embedded && {
            booking_source: BOOKING_SOURCE.embedded,
            guide_id: experienceDetail?.guide?.id
          })
        }
        await dispatch(attachUserPaymentMethod(payload))
        paymentMethodId = paymentMethod?.id
      }
      // Reuse the PaymentIntent from a previous attempt if we have one (e.g. the connection dropped
      // mid-charge and the guest is retrying). Only create a new intent when none exists, so a
      // dropped-then-resumed Submit can't mint a second charge.
      const isReusingIntent = !!paymentIntentRef.current?.clientSecret
      if (!isReusingIntent) {
        const res = await dispatch(
          createPaymentIntent(
            bookingInfo?.data?.id,
            bookingInfo?.data?.guest_token,
            appliedPromos.map((p) => p.code)
          )
        )
        if (!res?.client_secret) {
          // Intent not created (e.g. request failed) — nothing charged, allow a retry.
          setPaymentLoading(false)
          isSubmittingRef.current = false
          return
        }
        paymentIntentRef.current = { clientSecret: res.client_secret, incomeId: res?.income?.id }
      }

      const { clientSecret, incomeId } = paymentIntentRef.current
      const paymentMethodForConfirm = mode && accessToken ? selectedPaymentMethodId : paymentMethodId

      // When reusing an intent from a prior attempt, check whether it was already paid before
      // charging again: if that attempt's charge succeeded but its response was lost (connection
      // drop), the intent is already succeeded/processing/requires_capture — reuse it instead of
      // confirming (charging) a second time. A freshly created intent is never already charged, so
      // we skip this extra round-trip on the happy path.
      let paymentIntent = null
      if (isReusingIntent) {
        const { paymentIntent: existingIntent } = await stripe.retrievePaymentIntent(clientSecret)
        if (["succeeded", "processing", "requires_capture"].includes(existingIntent?.status)) {
          paymentIntent = existingIntent
        }
      }

      if (!paymentIntent) {
        const result = await stripe.confirmCardPayment(clientSecret, { payment_method: paymentMethodForConfirm })
        if (result?.error) {
          // Card declined / no charge — release the guard so the guest can correct and retry with
          // the same intent.
          setPaymentLoading(false)
          isSubmittingRef.current = false
          message.error(result.error.message)
          return
        }
        paymentIntent = result?.paymentIntent
      }

      if (!paymentIntent?.id) {
        // Nothing charged — allow a retry.
        setPaymentLoading(false)
        isSubmittingRef.current = false
        return
      }

      // The card has been charged (now or on a prior attempt). Keep isSubmittingRef locked from
      // here on: whatever capture returns, a second Submit must never create another charge.
      const captureRes = await dispatch(capturePayment(bookingInfo?.data?.id, incomeId))
      if (captureRes?.status === "succeeded" || captureRes?.status === "requested") {
        if (captureRes.status === "succeeded") {
          message.success("Booking successfully done!")
        }
        form?.resetFields()
        if (mode === BOOKING_MODES.embedded || !mode) {
          navigate(routes?.embeddedExperienceBookingConfirmation({ uuid: uuid || bookingInfo.data.uuid, id: bookingInfo.data.uuid }), {
            replace: true
          })
        } else {
          const isClientUser = userData?.role === "user"
          if (accessToken && isClientUser) {
            navigate(routes.clientBookingConfirmation({ id: bookingInfo.data.uuid }), { replace: true })
          } else {
            navigate(oldRoutes?.bookingConfirmation({ id: bookingInfo.data.uuid }), { replace: true })
          }
        }
      } else {
        // Charged but capture returned an unexpected status. Keep the guard locked (a retry would
        // double-charge) and tell the guest to contact support.
        setPaymentLoading(false)
        message.error("Your payment was received but we couldn't confirm your booking. Please contact support before trying again.")
      }
    } catch (error) {
      setPaymentLoading(false)
      if (error?.errorFields) {
        // Form validation failure — nothing charged, let the guest fix and resubmit.
        isSubmittingRef.current = false
        message.error("Please fill the required data!")
        form.scrollToField(error.errorFields?.[0]?.name, {
          behavior: "smooth",
          block: "center"
        })
      } else {
        // Network / unexpected error. A charge may or may not have gone through, so release the
        // guard to allow a retry — the retrievePaymentIntent check above makes that retry safe by
        // reusing paymentIntentRef instead of charging again.
        isSubmittingRef.current = false
        message.error("Something went wrong. Please check your connection and try again.")
      }
    }
  }

  const phone = contactFormValues?.phone_number
  const phoneValid = phone?.fullNumber || (typeof phone === "string" && phone.length > 0)
  const isStep2Complete =
    !!contactFormValues?.first_name?.trim() &&
    !!contactFormValues?.last_name?.trim() &&
    !!contactFormValues?.email?.trim() &&
    !!phoneValid &&
    (contactFormValues?.accessibility_enabled ? !!contactFormValues?.accessibility_notes?.trim() : true)

  const isNextDisabled = activeTab === 1 && !isStep2Complete

  return (
    <>
      <EmbeddedPageHeader />
      {(loading || bookingInfo?.loading || paymentLoading) && (
        <div className={styles.loaderOverlay}>
          <Spin size="large" />
        </div>
      )}
      <EmbeddedBookingLayout
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        onNext={handleNext}
        onPrevious={handlePrevious}
        mode={mode}
        selectedPaymentProvider={selectedPaymentProvider}
        isNextDisabled={isNextDisabled}
        paymentLoading={paymentLoading}
      >
        <Flex align="center" justify="space-between" className={`${styles.customTabs}  ${mode ? "mt-s" : ""} overflow-hidden`}>
          {[
            { label: "CONTACT INFORMATION", number: 1 },
            { label: "PAYMENT", number: 2 }
          ].map((item) => (
            <Flex
              gap={8}
              align="center"
              key={item.number}
              className={`${activeTab === item.number ? styles.activeTab : ""} py-xxs pl-l pr-xxs flex-1 ${styles.tabItem}`}
            >
              <Flex justify="center" align="center" className={`rounded-full ${styles.tabNumber}`}>
                {item.number < activeTab ? <Icon type="feather" name="Check" /> : item.number}
              </Flex>
              <Text className={styles.tabLabel}>{item.label}</Text>
            </Flex>
          ))}
        </Flex>
        <Row gutter={[10, 10]} className={`mt-xl ${styles.detailCardWrapper} ${mode === "embedded" ? styles.embeddedView : ""}`}>
          <Col span={24} lg={16}>
            <Form
              layout="vertical"
              form={form}
              onValuesChange={(_, allValues) => {
                if (activeTab === 1) setContactFormValues(allValues)
              }}
            >
              {activeTab === 1 && (
                <ContactInformationBlock
                  form={form}
                  setPhoneNumber={setPhoneNumber}
                  phoneNumber={phoneNumber}
                  initialValues={contactFormValues}
                />
              )}
              {activeTab === 2 && (
                <PaymentBlock
                  form={form}
                  uuid={uuid}
                  setPaymentLoading={setPaymentLoading}
                  mode={mode}
                  appliedPromos={appliedPromos}
                />
              )}
            </Form>
          </Col>
          <Col span={24} lg={8}>
            <BookingDetailCard
              bookNow={true}
              bookingButtonVisible={false}
              trip={experienceDetail}
              activeTab={activeTab}
              bookingErrors={bookingErrors}
              setBookingErrors={setBookingErrors}
              form={form}
              mode={mode}
              appliedPromos={appliedPromos}
              onPromoApplied={handlePromoApplied}
              onPromoRemoved={handlePromoRemoved}
              isEditingAddOns={isEditingAddOns}
              setIsEditingAddOns={setIsEditingAddOns}
              addOnsSnapshot={addOnsSnapshot}
              setAddOnsSnapshot={setAddOnsSnapshot}
              clientIdentifier={
                form.getFieldValue("email") ||
                form.getFieldValue("phone_number")?.fullNumber ||
                contactFormValues?.email ||
                contactFormValues?.phone_number
              }
            />
          </Col>
        </Row>
      </EmbeddedBookingLayout>
    </>
  )
}

EmbeddedExperienceBooking.propTypes = {
  mode: PropTypes.string
}

export default EmbeddedExperienceBooking
